import {
  type OnboardingStatusDto,
  type PlatformDistrictOnboardingRowDto,
  useOnboardingGetPlatform,
} from "@crowdcoursing/api/openapi";
import { Business as BusinessIcon } from "@mui/icons-material";
import { Avatar, Box, Drawer, Link as MuiLink, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useFeature } from "~hooks/useFeature";
import { type ColumnData, type RowData } from "~types/datagrid";
import { DataGrid } from "~ui/molecules/DataGrid/DataGrid";
import { BubbleOnboardingView } from "~ui/organisms/Onboarding/BubbleOnboardingView";
import { OnboardingStatusChip } from "~ui/organisms/Onboarding/OnboardingStatusChip";
import { OnboardingSummaryCards } from "~ui/organisms/Onboarding/OnboardingSummaryCards";
import { makeBubbleAvatarUrl } from "~utils/url";

interface DistrictRow {
  admins: number;
  bubbleId: string;
  dpaStatus?: OnboardingStatusDto;
  name: string;
  onboardingStatus?: OnboardingStatusDto;
  schoolCount: number;
  schoolsWithUsers: number;
  searchable: number;
  studentOnly: number;
}

type GridRow = RowData<DistrictRow>;

function statusNames(rows: PlatformDistrictOnboardingRowDto[], field: "dpaStatus" | "onboardingStatus"): string[] {
  return Array.from(new Set(rows.flatMap((row) => (row[field]?.name ? [row[field].name] : [])))).sort((left, right) =>
    left.localeCompare(right),
  );
}

export const DistrictOnboardingPanel = () => {
  const showResumes = useFeature("showResumes");
  const { data, status } = useOnboardingGetPlatform({});
  const [search, setSearch] = useState("");
  const [onboardingFilter, setOnboardingFilter] = useState("");
  const [dpaFilter, setDpaFilter] = useState("");
  const [selectedBubbleId, setSelectedBubbleId] = useState<string | null>(null);

  const districts = useMemo(() => data?.districts ?? [], [data?.districts]);
  const summary = useMemo(
    () => [
      { label: "Districts tracked", testId: "onboardingSummary-districtsTracked", value: districts.length },
      {
        label: "DPA not executed",
        testId: "onboardingSummary-dpaNotExecuted",
        value: districts.filter((district) => district.dpaStatus?.name !== "Executed").length,
      },
      showResumes
        ? {
            label: "Searchable Candidates",
            testId: "onboardingSummary-searchableCandidates",
            value: districts.reduce((total, district) => total + (district.metrics?.searchable ?? 0), 0),
          }
        : {
            label: "Schools",
            testId: "onboardingSummary-schools",
            value: districts.reduce((total, district) => total + (district.schoolCount ?? 0), 0),
          },
    ],
    [districts, showResumes],
  );
  const onboardingOptions = useMemo(() => statusNames(districts, "onboardingStatus"), [districts]);
  const dpaOptions = useMemo(() => statusNames(districts, "dpaStatus"), [districts]);

  const rows = useMemo((): GridRow[] => {
    const query = search.trim().toLowerCase();
    return districts.flatMap((district) => {
      const bubbleId = district.bubbleId;
      if (!bubbleId) {
        return [];
      }
      const name = district.name ?? "District";
      if (query && !name.toLowerCase().includes(query)) {
        return [];
      }
      if (onboardingFilter && district.onboardingStatus?.name !== onboardingFilter) {
        return [];
      }
      if (dpaFilter && district.dpaStatus?.name !== dpaFilter) {
        return [];
      }
      return [
        {
          admins: district.metrics?.admins ?? 0,
          bubbleId,
          dpaStatus: district.dpaStatus,
          name,
          onboardingStatus: district.onboardingStatus,
          rowId: bubbleId,
          schoolCount: district.schoolCount ?? 0,
          schoolsWithUsers: district.schoolsWithUsers ?? 0,
          searchable: district.metrics?.searchable ?? 0,
          studentOnly: district.metrics?.studentOnly ?? 0,
        },
      ];
    });
  }, [districts, dpaFilter, onboardingFilter, search]);

  const columns = useMemo((): ColumnData<DistrictRow>[] => {
    const allColumns: ColumnData<DistrictRow>[] = [
      {
        field: "name",
        flex: 1.2,
        headerName: "District",
        minWidth: 240,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0, py: 0.5 }}>
            <Avatar
              slotProps={{ img: { "aria-label": `${row.name} district avatar` } }}
              src={makeBubbleAvatarUrl(row.bubbleId)}
              sx={{ height: 32, width: 32 }}
            >
              <BusinessIcon fontSize="small" />
            </Avatar>
            <MuiLink
              aria-label={`Open ${row.name} onboarding`}
              component={RouterLink}
              data-testid={`onboardingDistrict-${row.bubbleId}`}
              sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
              to={`/admin?adminSelect=${row.bubbleId}&adminTab=onboarding`}
              underline="hover"
              variant="body2"
              onClick={(event) => event.stopPropagation()}
            >
              {row.name}
            </MuiLink>
          </Stack>
        ),
      },
      {
        field: "onboardingStatus",
        headerName: "Status",
        minWidth: 150,
        renderCell: ({ row }) => (
          <OnboardingStatusChip status={row.onboardingStatus} testId={`onboardingStatus-${row.bubbleId}`} />
        ),
        sortable: false,
      },
      {
        field: "dpaStatus",
        headerName: "DPA",
        minWidth: 160,
        renderCell: ({ row }) => (
          <OnboardingStatusChip status={row.dpaStatus} testId={`onboardingDpa-${row.bubbleId}`} />
        ),
        sortable: false,
      },
      { align: "right", field: "schoolCount", headerAlign: "right", headerName: "Schools", minWidth: 110 },
      {
        align: "right",
        field: "schoolsWithUsers",
        headerAlign: "right",
        headerName: "Schools with users",
        minWidth: 160,
      },
      { align: "right", field: "studentOnly", headerAlign: "right", headerName: "Student-only", minWidth: 130 },
      { align: "right", field: "searchable", headerAlign: "right", headerName: "Searchable Candidates", minWidth: 190 },
      { align: "right", field: "admins", headerAlign: "right", headerName: "Admins", minWidth: 100 },
    ];
    return allColumns.filter(
      (column) => showResumes || (column.field !== "studentOnly" && column.field !== "searchable"),
    );
  }, [showResumes]);

  const closeDrawer = () => {
    setSelectedBubbleId(null);
  };

  return (
    <Box sx={{ maxWidth: "100%", minWidth: 0, pt: 1 }}>
      <Typography sx={{ mb: 0.5 }} variant="h6">
        Onboarding
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 900, mb: 2 }} variant="body2">
        Active districts with their saved onboarding status and DPA. Click a row to review schools and role counts.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <OnboardingSummaryCards stats={summary} />
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <TextField
          data-testid="onboardingDistrictSearch"
          label="Search district"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <TextField
          select
          data-testid="onboardingStatusFilter"
          label="Status"
          size="small"
          sx={{ minWidth: 180 }}
          value={onboardingFilter}
          onChange={(event) => setOnboardingFilter(event.target.value)}
        >
          <MenuItem value="">All statuses</MenuItem>
          {onboardingOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          data-testid="onboardingDpaFilter"
          label="DPA"
          size="small"
          sx={{ minWidth: 180 }}
          value={dpaFilter}
          onChange={(event) => setDpaFilter(event.target.value)}
        >
          <MenuItem value="">All DPA</MenuItem>
          {dpaOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <DataGrid<DistrictRow>
        autoHeight
        columns={columns}
        disableRowSelectionOnClick
        rows={rows}
        status={status}
        sx={{ "& .MuiDataGrid-cell": { alignItems: "center", display: "flex" } }}
        onRowClick={({ row }) => {
          setSelectedBubbleId(row.bubbleId);
        }}
      />
      <Drawer anchor="right" open={Boolean(selectedBubbleId)} onClose={closeDrawer}>
        <Box sx={{ maxWidth: "100vw", p: 2, width: 720 }}>
          {selectedBubbleId ? <BubbleOnboardingView bubbleId={selectedBubbleId} /> : null}
        </Box>
      </Drawer>
    </Box>
  );
};
